import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import InlineSlotPicker from "./InlineSlotPicker";
import { formatDateForDisplay, formatTimeForDisplay } from "../../utils/availability";
import { previewBookingPayment } from "../../utils/bookingPayment";
import { cloudinaryImageUrl, IMAGE_WIDTH } from "../../utils/cloudinaryDelivery";
import { resolveServicePaymentMode } from "../../utils/paymentMode";
import { resolveServiceDescription } from "../../utils/serviceDescription";
import { bookingPageCategoryGroups } from "../../utils/serviceCategoryOrder";

const LEGACY_DEFAULT_CTA_LABELS = new Set([
  "book service",
  "book (pay in cash)",
]);

const formatCurrency = (value) => {
  if (value === undefined || value === null) return "$0.00";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    Number(value)
  );
};

// "5 hrs 30 min" rather than "330 min", which is how the design writes the
// longer appointments that make up most of a booking page.
const formatDuration = (minutes) => {
  const total = Number(minutes);
  if (!Number.isFinite(total) || total <= 0) return "";
  const hours = Math.floor(total / 60);
  const remainder = total % 60;
  if (!hours) return `${remainder} min`;
  const hoursPart = `${hours} hr${hours === 1 ? "" : "s"}`;
  return remainder ? `${hoursPart} ${remainder} min` : hoursPart;
};

// The slot picker hands back the raw key it builds its buttons from; the CTA
// only wants the day and the start time, not the full range.
const formatSlotForCta = (slot) => {
  const date = formatDateForDisplay(slot?.date || "");
  const start = String(slot?.timeSlot || "").split("-")[0];
  const time = start ? formatTimeForDisplay(start) : "";
  return [date, time].filter(Boolean).join(", ");
};

// The line under the button: how checkout takes payment for this service.
// Read off the same preview the price summary above it shows.
const paymentDetailCopyFor = (preview) => {
  if (preview.isCompletelyFreeService) return "No payment needed";
  if (preview.isCashOnly) return "Pay at appointment";
  if (preview.offersFreeReservation) return "Reserve free or pay online at checkout";
  if (preview.canChoosePaymentMethod) return "Pay online or at appointment";
  return "Pay online at checkout";
};

// A service that only happens at the pro's own place can't be booked until the
// pro has a full address; the server refuses it too. Unset means at_location,
// as it does in BookingForm.
export const isServiceBookableOnline = (service, { proLocationBookable = true } = {}) => {
  if (proLocationBookable) return true;
  const type = String(service?.locationType || "at_location").trim().toLowerCase();
  return type !== "at_location";
};

const categoryHeadingId = (index) => `services-category-${index}`;

// Photo cards show this many services per category until "See all".
export const GRID_PREVIEW_COUNT = 2;

const pluralServices = (count) => `${count} service${count === 1 ? "" : "s"}`;

const ChevronIcon = () => (
  <svg className="service-line__icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="m6 3.5 4.5 4.5L6 12.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CheckIcon = () => (
  <svg className="service-line__icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="m3.5 8.5 3 3 6-7"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// A smooth jump takes a moment; while it runs, the chip the client tapped
// stays the active one instead of flicking through every heading it passes.
const JUMP_SETTLE_MS = 1200;
const HEADING_AT_TOP_SLACK = 24;

// The row of category chips above a long menu. On a phone it pins under the
// top of the screen; the active chip follows the heading at the top, and a tap
// scrolls to that heading.
const CategoryJumpLinks = ({ groups }) => {
  const navRef = useRef(null);
  const scrollerRef = useRef(null);
  const jumpLockUntil = useRef(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [pinned, setPinned] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let frame = 0;
    const update = () => {
      frame = 0;
      const nav = navRef.current;
      if (!nav) return;
      const navBox = nav.getBoundingClientRect();
      setPinned(window.getComputedStyle(nav).position === "sticky" && navBox.top <= 0);
      if (Date.now() < jumpLockUntil.current) return;
      // A jump leaves the heading a little below the row (its scroll margin),
      // so "at the top" allows for that gap.
      const line = Math.max(navBox.bottom, 0) + HEADING_AT_TOP_SLACK;
      let next = 0;
      groups.forEach((_, index) => {
        const heading = document.getElementById(categoryHeadingId(index));
        if (heading && heading.getBoundingClientRect().top <= line) next = index;
      });
      setActiveIndex(next);
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    update();
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [groups]);

  // Keep the active chip in view inside the row, without moving the page.
  useEffect(() => {
    const scroller = scrollerRef.current;
    const chip = scroller?.querySelector(`[data-category-index="${activeIndex}"]`);
    if (!scroller || !chip || typeof scroller.scrollTo !== "function") return;
    const start = chip.offsetLeft;
    const end = start + chip.offsetWidth;
    if (start < scroller.scrollLeft || end > scroller.scrollLeft + scroller.clientWidth) {
      scroller.scrollTo({ left: Math.max(0, start - 16), behavior: prefersReducedMotion() ? "auto" : "smooth" });
    }
  }, [activeIndex]);

  const jumpTo = (event, index) => {
    event.preventDefault();
    const heading = document.getElementById(categoryHeadingId(index));
    setActiveIndex(index);
    jumpLockUntil.current = Date.now() + JUMP_SETTLE_MS;
    if (!heading) return;
    heading.scrollIntoView?.({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start" });
    heading.focus?.({ preventScroll: true });
  };

  return (
    <nav
      ref={navRef}
      className={`services-category-nav${pinned ? " is-pinned" : ""}`}
      aria-label="Service categories"
    >
      <div ref={scrollerRef} className="services-category-nav__scroller">
        {groups.map((group, index) => (
          <a
            key={group.key || "other"}
            href={`#${categoryHeadingId(index)}`}
            className="services-category-chip"
            data-category-index={index}
            aria-current={index === activeIndex ? "true" : undefined}
            onClick={(event) => jumpTo(event, index)}
          >
            {group.label}
          </a>
        ))}
      </div>
    </nav>
  );
};

const ProfileServicesSection = ({
  services = [],
  className = "",
  firstName = "This professional",
  hasCustomName = false,
  // The pro's own payment mode, "card" or "cash_only". Each service is shown
  // under resolveServicePaymentMode of it, the mode the booking form gets.
  paymentMode = "card",
  servicesHeading = "",
  ctaText = "",
  cancellationNote = "",
  isEditorMode = false,
  activeThemeTarget = "",
  onThemeTargetSelect,
  onInlineTextChange,
  onBookService,
  professionalId = "",
  availability = [],
  bookingDisabled = false,
  // Set by the illustrative booking page at /example. A demo has no booking to
  // submit, so the CTA becomes a link to this href instead — but the slot
  // picker stays browsable, and opens with a day and time already chosen,
  // because seeing it work is the point of the page.
  demoCtaHref = "",
  // False when the pro has no open hours yet: the services still show, but the
  // panel says so instead of offering times.
  acceptingBookings = true,
  // False when the pro has no full address, so their own place can't be booked.
  proLocationBookable = true,
  // Who the "not taking bookings" line is about: the business name, or the
  // person's name. Falls back to "Your stylist".
  businessLabel = "",
  // The pro's "Show categories on my booking page" switch. Even when it's on,
  // the list stays flat until there are two categories to tell apart.
  showCategories = false,
  // "card" and "minimal" draw the usual rows. A Custom page can also have
  // "list" (one line per service, no photo) or "grid" (photo cards two
  // across); the theme only hands those over on Custom pages.
  layout = "card",
  // From a ?service=<id> link: the service the page opens on. An id that isn't
  // on the list (or can't be booked online) falls back to the first, as usual.
  initialServiceId = "",
}) => {
  const [selectedServiceId, setSelectedServiceId] = useState(initialServiceId || "");
  const [pendingSlot, setPendingSlot] = useState(null);
  // Photo-card categories opened with "See all", by group key.
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const [focusServiceId, setFocusServiceId] = useState("");
  const isListLayout = layout === "list";
  const isGridLayout = layout === "grid";
  const isDemo = Boolean(demoCtaHref);
  const pickerDisabled = isEditorMode || (bookingDisabled && !isDemo);

  // One service is always in focus, because the panel beside the list prices
  // and times that one service. The first is the page's own running order.
  // A service that can't be booked online is never the one in focus.
  const bookableServices = useMemo(
    () => services.filter((service) => isServiceBookableOnline(service, { proLocationBookable })),
    [proLocationBookable, services],
  );
  const selectedService = useMemo(() => {
    if (!bookableServices.length) return null;
    return (
      bookableServices.find((service) => service._id === selectedServiceId) ||
      bookableServices[0]
    );
  }, [bookableServices, selectedServiceId]);

  // Sanitizing parses markup, so do it once per list rather than per render.
  const descriptions = useMemo(
    () =>
      new Map(services.map((service) => [service, resolveServiceDescription(service.description)])),
    [services],
  );

  const categoryLayout = useMemo(
    () => (showCategories ? bookingPageCategoryGroups(services) : null),
    [showCategories, services],
  );
  const showCategoryHeadings = Boolean(categoryLayout?.showHeadings);

  // Times belong to the service they were found under, so switching service
  // drops the slot rather than carrying a stale one into the booking modal.
  useEffect(() => {
    setPendingSlot(null);
  }, [selectedService?._id]);

  const handleSlotChange = useCallback((slot) => {
    setPendingSlot(slot);
  }, []);

  const handleServiceSelect = (serviceId) => {
    if (isEditorMode) return;
    setSelectedServiceId(serviceId);
  };

  // "See all" goes away once it's used, so focus moves to the first card it
  // brought in rather than being dropped.
  useEffect(() => {
    if (!focusServiceId) return;
    document.getElementById(`service-card-${focusServiceId}`)?.focus();
    setFocusServiceId("");
  }, [focusServiceId]);

  const handleShowAll = (groupKey, firstHiddenId) => {
    setExpandedGroups((previous) => new Set(previous).add(groupKey));
    setFocusServiceId(firstHiddenId || "");
  };

  const sectionClassName = ["services-list", className].filter(Boolean).join(" ");
  const providerLabel = hasCustomName ? firstName : "this pro";
  const defaultServicesHeading = `Book time with ${providerLabel}`;
  const defaultCtaText = "Book";
  const resolvedServicesHeading =
    typeof servicesHeading === "string" && servicesHeading.trim()
      ? servicesHeading.trim()
      : defaultServicesHeading;
  const customCtaText = typeof ctaText === "string" ? ctaText.trim() : "";
  const resolvedCtaText =
    customCtaText && !LEGACY_DEFAULT_CTA_LABELS.has(customCtaText.toLowerCase())
      ? customCtaText
      : defaultCtaText;
  const selectedServicePaymentMode = resolveServicePaymentMode(selectedService, paymentMode);
  // With no service to book (the editor, before one is added) the line falls
  // back to the pro's own mode.
  const paymentDetailCopy = selectedService
    ? paymentDetailCopyFor(
        previewBookingPayment(selectedService, {
          paymentMode: selectedServicePaymentMode,
          proLocationBookable,
        }),
      )
    : paymentMode === "cash_only"
      ? "Pay at appointment"
      : "Pay online at checkout";
  const isTargetActive = (target) => isEditorMode && activeThemeTarget === target;
  const notTakingBookingsCopy = `${
    (typeof businessLabel === "string" && businessLabel.trim()) || "Your stylist"
  } isn't taking online bookings yet. Check back soon.`;
  // The editor and the demo page keep their own panel; only a real visitor is
  // told there is nothing to book yet.
  const showNotTakingBookings =
    !isEditorMode && !isDemo && (!acceptingBookings || !selectedService);

  const hasSlot = Boolean(pendingSlot?.date && pendingSlot?.timeSlot);
  const ctaLabel = hasSlot
    ? `${resolvedCtaText} ${formatSlotForCta(pendingSlot)}`
    : resolvedCtaText;
  // A live page books the time picked in the panel and nothing else. Until
  // there is one, Book stays off and says what's missing, rather than opening
  // checkout on a second, empty date and time picker of its own.
  const awaitingSlot = !isEditorMode && !hasSlot;

  const handleEditorBookClick = (event) => {
    if (!isEditorMode) {
      return;
    }
    event.preventDefault();
    onThemeTargetSelect?.("accent");
  };

  const handleInlineTextChange = (field) => (event) => {
    if (!isEditorMode) return;
    onInlineTextChange?.(field, event.currentTarget.textContent || "");
  };

  const handleInlineEditInteraction = (event) => {
    if (!isEditorMode) return;
    event.stopPropagation();
  };

  const handleInlineTextKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.currentTarget.blur();
    }
  };

  const editableCtaLabel =
    isEditorMode ? (
      <span
        data-inline-edit-field="primaryCtaText"
        contentEditable
        suppressContentEditableWarning
        onBlur={handleInlineTextChange("primaryCtaText")}
        onMouseDown={handleInlineEditInteraction}
        onClick={handleInlineEditInteraction}
        onKeyDown={handleInlineTextKeyDown}
        role="textbox"
        aria-label="Edit main call-to-action text"
      >
        {resolvedCtaText}
      </span>
    ) : awaitingSlot ? (
      "Pick a time"
    ) : (
      ctaLabel
    );

  const renderServiceRow = (service) => {
    const isSelected = service._id === selectedService?._id;
    const isBookable = isServiceBookableOnline(service, { proLocationBookable });
    const durationCopy = formatDuration(service.duration);
    const isFree = !Number(service.price);
    // Checkout never takes a deposit on a cash-only service: it's all paid at
    // the appointment.
    const takesDeposit =
      Number(service.deposit) > 0 &&
      resolveServicePaymentMode(service, paymentMode) !== "cash_only";
    const description = descriptions.get(service);

    return (
      <li
        key={service._id}
        className={[
          "service-row",
          isSelected ? "is-selected" : "",
          isBookable ? "" : "is-unavailable",
          isTargetActive("serviceCard") ? "is-theme-target-active" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        data-theme-target={isEditorMode ? "serviceCard" : undefined}
      >
        {service.images?.[0]?.url && (
          <div className="service-media" data-theme-target={isEditorMode ? "serviceCard" : undefined}>
            <img src={cloudinaryImageUrl(service.images[0].url, { width: IMAGE_WIDTH.card })} alt={`${service.title} preview`} loading="lazy" />
          </div>
        )}

        <div className="service-details">
          <h4 data-theme-target={isEditorMode ? "text" : undefined}>{service.title}</h4>
          {description?.text && (
            <p data-theme-target={isEditorMode ? "text" : undefined}>{description.text}</p>
          )}
          {description?.html && (
            <div
              className="service-description"
              data-theme-target={isEditorMode ? "text" : undefined}
              dangerouslySetInnerHTML={{ __html: description.html }}
            />
          )}
          <div className="service-meta" data-theme-target={isEditorMode ? "text" : undefined}>
            {durationCopy && <span>{durationCopy}</span>}
            <span>
              {takesDeposit
                ? `${formatCurrency(service.deposit)} deposit`
                : "No deposit required"}
            </span>
          </div>
        </div>

        <div className="service-choose">
          <span
            className={`service-price${isFree ? " service-price--free" : ""}`}
            data-theme-target={isEditorMode ? "text" : undefined}
          >
            {isFree ? "Free" : formatCurrency(service.price)}
          </span>
          {!isBookable ? (
            <span className="service-unavailable-pill">Not bookable online yet</span>
          ) : isSelected ? (
            <span className="service-selected-pill">Selected</span>
          ) : (
            <button
              type="button"
              className="service-choose-btn"
              onClick={() => handleServiceSelect(service._id)}
              disabled={isEditorMode}
              aria-label={`Choose ${service.title}`}
            >
              Choose
            </button>
          )}
        </div>
      </li>
    );
  };

  const depositCopy = (service) =>
    service.deposit && service.deposit > 0
      ? `${formatCurrency(service.deposit)} deposit`
      : "No deposit required";

  // "list": one line per service, name and details on the left, price and an
  // arrow on the right. The whole line chooses the service, as Choose does.
  const renderServiceLine = (service) => {
    const isSelected = service._id === selectedService?._id;
    const isBookable = isServiceBookableOnline(service, { proLocationBookable });
    const isFree = !Number(service.price);
    const meta = [formatDuration(service.duration), depositCopy(service)]
      .filter(Boolean)
      .join(" · ");
    const isControl = isBookable && !isEditorMode;
    const Line = isControl ? "button" : "div";

    return (
      <li
        key={service._id}
        className={[
          "service-line",
          isSelected ? "is-selected" : "",
          isBookable ? "" : "is-unavailable",
          isTargetActive("serviceCard") ? "is-theme-target-active" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        data-theme-target={isEditorMode ? "serviceCard" : undefined}
      >
        <Line
          className="service-line__main"
          {...(isControl
            ? {
                type: "button",
                onClick: () => handleServiceSelect(service._id),
                "aria-pressed": isSelected,
              }
            : {})}
        >
          <span className="service-line__text">
            <span className="service-line__title" data-theme-target={isEditorMode ? "text" : undefined}>
              {service.title}
            </span>
            {meta && <span className="service-line__meta">{meta}</span>}
          </span>
          <span className="service-line__end">
            <span
              className={`service-price${isFree ? " service-price--free" : ""}`}
              data-theme-target={isEditorMode ? "text" : undefined}
            >
              {isFree ? "Free" : formatCurrency(service.price)}
            </span>
            {!isBookable ? (
              <span className="service-line__note">Not bookable online yet</span>
            ) : isSelected ? (
              <CheckIcon />
            ) : (
              <ChevronIcon />
            )}
          </span>
        </Line>
      </li>
    );
  };

  // "grid": photo cards two across, photo on top.
  const renderServiceCard = (service) => {
    const isSelected = service._id === selectedService?._id;
    const isBookable = isServiceBookableOnline(service, { proLocationBookable });
    const durationCopy = formatDuration(service.duration);
    const isFree = !Number(service.price);
    const description = descriptions.get(service);

    return (
      <li
        key={service._id}
        id={`service-card-${service._id}`}
        tabIndex={-1}
        className={[
          "service-card",
          isSelected ? "is-selected" : "",
          isBookable ? "" : "is-unavailable",
          isTargetActive("serviceCard") ? "is-theme-target-active" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        data-theme-target={isEditorMode ? "serviceCard" : undefined}
      >
        {service.images?.[0]?.url && (
          <div className="service-card__media">
            <img
              src={cloudinaryImageUrl(service.images[0].url, { width: IMAGE_WIDTH.card })}
              alt={`${service.title} preview`}
              loading="lazy"
            />
          </div>
        )}
        <div className="service-card__body">
          <h4 data-theme-target={isEditorMode ? "text" : undefined}>{service.title}</h4>
          {description?.text && (
            <p className="service-card__description" data-theme-target={isEditorMode ? "text" : undefined}>
              {description.text}
            </p>
          )}
          {description?.html && (
            <div
              className="service-description service-card__description"
              data-theme-target={isEditorMode ? "text" : undefined}
              dangerouslySetInnerHTML={{ __html: description.html }}
            />
          )}
          {durationCopy && (
            <span className="service-card__meta" data-theme-target={isEditorMode ? "text" : undefined}>
              {durationCopy}
            </span>
          )}
          <div className="service-card__foot">
            <span
              className={`service-price${isFree ? " service-price--free" : ""}`}
              data-theme-target={isEditorMode ? "text" : undefined}
            >
              {isFree ? "Free" : formatCurrency(service.price)}
            </span>
            {!isBookable ? (
              <span className="service-unavailable-pill">Not bookable online yet</span>
            ) : isSelected ? (
              <span className="service-selected-pill">Selected</span>
            ) : (
              <button
                type="button"
                className="service-choose-btn"
                onClick={() => handleServiceSelect(service._id)}
                disabled={isEditorMode}
                aria-label={`Choose ${service.title}`}
              >
                Choose
              </button>
            )}
          </div>
        </div>
      </li>
    );
  };

  // One category's services (or the whole flat list) in the page's layout.
  // Only photo cards in a category stop at GRID_PREVIEW_COUNT; the selected
  // service is never hidden behind "See all".
  const renderServiceGroup = (groupServices, { groupKey = "", label = "", capped = false } = {}) => {
    if (isListLayout) {
      return <ul className="service-lines">{groupServices.map(renderServiceLine)}</ul>;
    }
    if (!isGridLayout) {
      return <ul>{groupServices.map(renderServiceRow)}</ul>;
    }
    const hidden = capped ? groupServices.slice(GRID_PREVIEW_COUNT) : [];
    const showAll =
      hidden.length === 0 ||
      isEditorMode ||
      expandedGroups.has(groupKey) ||
      hidden.some((service) => service._id === selectedService?._id);
    const shown = showAll ? groupServices : groupServices.slice(0, GRID_PREVIEW_COUNT);
    return (
      <>
        <ul className="service-cards">{shown.map(renderServiceCard)}</ul>
        {!showAll && (
          <button
            type="button"
            className="service-cards__more"
            onClick={() => handleShowAll(groupKey, hidden[0]?._id)}
            aria-label={label ? `See all ${pluralServices(groupServices.length)} in ${label}` : undefined}
          >
            See all {pluralServices(groupServices.length)}
          </button>
        )}
      </>
    );
  };

  return (
    <section
      id="services-section"
      className={`${sectionClassName}${isTargetActive("servicesSection") ? " is-theme-target-active" : ""}`}
      data-theme-target={isEditorMode ? "servicesSection" : undefined}
    >
      <div className="services-layout">
        <div className="services-layout__list">
          <header>
            <h3
              data-theme-target={isEditorMode ? "text" : undefined}
              contentEditable={isEditorMode}
              suppressContentEditableWarning={isEditorMode}
              onBlur={handleInlineTextChange("servicesHeading")}
              onMouseDown={handleInlineEditInteraction}
              onClick={handleInlineEditInteraction}
              onKeyDown={handleInlineTextKeyDown}
              role={isEditorMode ? "textbox" : undefined}
              aria-label={isEditorMode ? "Edit services section heading" : undefined}
            >
              {resolvedServicesHeading}
            </h3>
            <p data-theme-target={isEditorMode ? "text" : undefined}>
              You&apos;ll get a confirmation email with next steps
              as soon as it&apos;s booked.
            </p>
          </header>

          {showCategoryHeadings ? (
            <>
              {categoryLayout.showJumpLinks && <CategoryJumpLinks groups={categoryLayout.groups} />}
              {categoryLayout.groups.map((group, index) => (
                <section
                  key={group.key || "other"}
                  className="services-category"
                  aria-labelledby={categoryHeadingId(index)}
                >
                  <h4
                    id={categoryHeadingId(index)}
                    className="services-category__title"
                    tabIndex={-1}
                    data-theme-target={isEditorMode ? "text" : undefined}
                  >
                    {group.label}
                    {(isListLayout || isGridLayout) && (
                      <span className="services-category__count">
                        {" · "}
                        {pluralServices(group.services.length)}
                      </span>
                    )}
                  </h4>
                  {renderServiceGroup(group.services, {
                    groupKey: group.key || "other",
                    label: group.label,
                    capped: true,
                  })}
                </section>
              ))}
            </>
          ) : services.length === 0 ? (
            <ul>
              <li className="service-row empty-state">
                <p>{notTakingBookingsCopy}</p>
              </li>
            </ul>
          ) : (
            renderServiceGroup(services)
          )}
        </div>

        {(services.length > 0 || isEditorMode) && (
          <aside
            className="booking-panel"
            aria-labelledby="booking-panel-heading"
          >
            <h3 className="booking-panel__title" id="booking-panel-heading">
              Pick a time
            </h3>
            {(selectedService || isEditorMode) && (
              <p className="booking-panel__service">
                {selectedService
                  ? [selectedService.title, formatDuration(selectedService.duration)]
                      .filter(Boolean)
                      .join(" — ")
                  : "Add a service and your clients pick a time here."}
              </p>
            )}

            {showNotTakingBookings ? (
              <p className="booking-panel__note booking-panel__empty" role="status">
                {notTakingBookingsCopy}
              </p>
            ) : pickerDisabled || !selectedService ? (
              <p className="booking-panel__note booking-panel__note--placeholder">
                Your open days and times show here on your live page.
              </p>
            ) : (
              <InlineSlotPicker
                professionalId={professionalId}
                service={selectedService}
                availability={availability}
                value={pendingSlot}
                onChange={handleSlotChange}
                paymentMode={selectedServicePaymentMode}
                proLocationBookable={proLocationBookable}
                demoMode={isDemo}
                // The demo page is read at a glance, so it opens on the next
                // opening already chosen: a visitor sees the finished booking —
                // day, time and what it costs — without touching anything.
                autoSelectFirstOpening={isDemo}
                // A live page opens on the first day with openings, so there
                // are times to pick from; which one is the visitor's call.
                autoSelectFirstDay={!isDemo}
              />
            )}

            {showNotTakingBookings ? null : isDemo ? (
              <Link className="book-service-btn" to={demoCtaHref}>
                {ctaLabel}
              </Link>
            ) : (
              <button
                type="button"
                className={`book-service-btn${isTargetActive("accent") ? " is-theme-target-active" : ""}`}
                data-theme-target={isEditorMode ? "accent" : undefined}
                disabled={awaitingSlot}
                onClick={(event) => {
                  if (isEditorMode) {
                    if (
                      event.target?.closest?.("[data-inline-edit-field='primaryCtaText']")
                    ) {
                      return;
                    }
                    handleEditorBookClick(event);
                    return;
                  }
                  if (!selectedService || !hasSlot) return;
                  onBookService?.(selectedService, pendingSlot);
                }}
              >
                {editableCtaLabel}
              </button>
            )}

            {!showNotTakingBookings && (
              <p className="service-payment-detail" data-theme-target={isEditorMode ? "text" : undefined}>
                {paymentDetailCopy}
              </p>
            )}
            {cancellationNote && !showNotTakingBookings && (
              <p className="booking-panel__note" data-theme-target={isEditorMode ? "text" : undefined}>
                {cancellationNote}
              </p>
            )}
          </aside>
        )}
      </div>
    </section>
  );
};

export default ProfileServicesSection;
